import { t } from "i18next";
import { v4 as uuidv4 } from "uuid";
import { ng } from "@common/ngds";
import { addAlert } from "../ui/uiSlice";

/*
Warnings logged by the calculation engine.

Errors and warnings leave the engine through the same log (Utils/log.proto) but reach the client by
two different routes, which is why they need two different readers:

  - An ERROR fails the run. doCodeCalculation (server/calculation/designs.ts) rejects a 200 response
    whose project log holds one, so it never arrives with a result; it comes back as the `errors`
    array of a failed call and is surfaced from ProjectSlice.calcError. See clientErrorsFromProjectLog
    and getLogErrors in @common/helpers/error, which filter to LOG_TYPE_ERROR for exactly that reason.

  - A WARNING fails nothing. The results are valid and are consumed as usual, and the warning rides
    along inside the returned project's own log. Nothing used to read it, so it was invisible. This
    module is that reader.

Per the log contract an entry is meant for the end user only when it carries a `label` (the i18next
key); an entry with only `text` is internal debugging and is skipped here, the same rule the error
path applies. `values` carries the supplementary numbers so the translated string can interpolate
them - the engine's own English `text` is never shown, so anything the user needs to see has to be
in the label's translation, with the numbers substituted in.
*/

type LogJson = ng.log.Log_json | null | undefined;
type LogEntryJson = ng.log.LogEntry_json;
type LogValueJson = ng.log.LogValue_json;

// The log lives on the project's inner ng.project.Data, so it is `project.data.log` - one level
// deeper than the project the thunks hand around. Kept here, typed, rather than spelled out at each
// call site: runCalcThunk returns `any`, so a wrong path there is not a compile error, it is a
// silent no-op that never shows a warning.
type ProjectJson = ng.frontend.column.Project_json | null | undefined;

export const logOfProject = (project: ProjectJson): LogJson => project?.data?.log;

// The project arrives as generated *_json, where a proto enum is its name rather than its number.
// A caller holding a real ng.log.Log instance would have the number instead, so accept both.
const isWarning = (entry: LogEntryJson): boolean =>
  (entry.type as unknown) === "LOG_TYPE_WARNING" || (entry.type as unknown) === ng.log.LogType.LOG_TYPE_WARNING;

// LogValue is a oneof, so exactly one of these fields is set. JsonNumber is a number or its string
// form depending on how the value round-tripped, hence the Number() on the numeric branches.
const interpolationValues = (values: LogValueJson[] | undefined): Record<string, string | number | boolean> => {
  const out: Record<string, string | number | boolean> = {};
  for (const v of values ?? []) {
    if (!v.key) continue;
    if (v.stringValue !== undefined) out[v.key] = v.stringValue;
    else if (v.doubleValue !== undefined) out[v.key] = Number(v.doubleValue);
    else if (v.integerValue !== undefined) out[v.key] = Number(v.integerValue);
    else if (v.boolValue !== undefined) out[v.key] = v.boolValue;
  }
  return out;
};

/**
 * The localised, de-duplicated warnings in a calculation's project log, in the order they were
 * logged. A label with no translation resolves to the label key itself — the same fallback the error
 * path takes, and the reason a new engine warning has to ship with its four locale entries.
 *
 * The engine logs a warning once per element, load combination and axis pass, so the same condition
 * can be reported many times with identical wording; those collapse to one.
 */
export const localiseCalcWarnings = (log: LogJson): string[] => {
  const seen = new Set<string>();
  const messages: string[] = [];

  for (const entry of log?.entries ?? []) {
    if (!isWarning(entry) || !entry.label) continue;

    const message = t(`${entry.label}.label`, {
      defaultValue: entry.label,
      ...interpolationValues(entry.values),
    });
    if (typeof message !== "string" || message.trim() === "" || seen.has(message)) continue;

    seen.add(message);
    messages.push(message);
  }

  return messages;
};

/**
 * Raise the warnings in a calculated project's log as alerts. Called by the calculation thunks on a
 * successful run, with the project the run answered with and the thunk's own dispatch.
 *
 * The alerts are dispatched through the caller's dispatch rather than through AlertMessage (which
 * reaches for the store singleton): projectSlice is imported while app/store.ts is still evaluating,
 * so an import of the store from here closes an import cycle and the slices lose their
 * initialisation race. See uiSlice for the meaning of the "calc" source.
 *
 * These are kept on screen rather than faded: a warning exists to be read next to a result the user
 * is already looking at (a moment utilization inflated by a near-buckling magnification, say), and a
 * few seconds is not long enough to notice one. calcRunStarted retracts them when the next run
 * begins, so nothing stale survives a re-calculation.
 */
export const showCalcWarnings = (project: ProjectJson, dispatch: (action: ReturnType<typeof addAlert>) => void): void => {
  for (const message of localiseCalcWarnings(logOfProject(project))) {
    dispatch(
      addAlert({
        id: uuidv4(),
        type: "warning",
        status: t("warning_.label", "Warning"),
        context: undefined,
        text: message,
        keep: true,
        source: "calc",
      }),
    );
  }
};
